import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, Save, Share, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SafetyPlan {
  id?: string;
  warning_signs: string[];
  internal_coping: string[];
  distraction_activities: string[];
  people_to_contact: Array<{name: string; relationship: string; phone: string; preferred_contact_method: string}>;
  professional_contacts: Array<{name: string; role: string; phone: string; notes: string}>;
  remove_means_plan: string[];
  reasons_for_living: string[];
  steps_if_plan_fails: string[];
}

interface SafetyPlanBuilderProps {
  user: any;
}

export const SafetyPlanBuilder = ({ user }: SafetyPlanBuilderProps) => {
  const [safetyPlan, setSafetyPlan] = useState<SafetyPlan>({
    warning_signs: [],
    internal_coping: [],
    distraction_activities: [],
    people_to_contact: [],
    professional_contacts: [],
    remove_means_plan: [],
    reasons_for_living: [],
    steps_if_plan_fails: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSafetyPlan();
  }, [user?.id]);

  const loadSafetyPlan = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('safety_plans')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setSafetyPlan(data as unknown as SafetyPlan);
      }
    } catch (error: any) {
      console.error('Error loading safety plan:', error);
      toast.error(error.message || 'Failed to load safety plan');
    } finally {
      setLoading(false);
    }
  };

  const saveSafetyPlan = async () => {
    if (!user?.id) {
      toast.error('Please log in to save your safety plan');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('safety_plans')
        .upsert({
          ...safetyPlan,
          user_id: user.id
        });

      if (error) throw error;
      toast.success('Safety plan saved successfully ✓');
    } catch (error: any) {
      console.error('Error saving safety plan:', error);
      toast.error(error.message || 'Failed to save safety plan');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (section: keyof SafetyPlan, value: string | object) => {
    setSafetyPlan(prev => ({
      ...prev,
      [section]: [...(prev[section] as any[]), value]
    }));
  };

  const removeItem = (section: keyof SafetyPlan, index: number) => {
    setSafetyPlan(prev => ({
      ...prev,
      [section]: (prev[section] as any[]).filter((_, i) => i !== index)
    }));
  };

  const exportPlan = () => {
    const planText = `
SAFETY PLAN

WARNING SIGNS:
${safetyPlan.warning_signs.map(sign => `• ${sign}`).join('\n')}

INTERNAL COPING STRATEGIES:
${safetyPlan.internal_coping.map(strategy => `• ${strategy}`).join('\n')}

DISTRACTION ACTIVITIES:
${safetyPlan.distraction_activities.map(activity => `• ${activity}`).join('\n')}

PEOPLE TO CONTACT:
${safetyPlan.people_to_contact.map(person => `• ${person.name} (${person.relationship}) - ${person.phone}`).join('\n')}

PROFESSIONAL CONTACTS:
${safetyPlan.professional_contacts.map(contact => `• ${contact.name} (${contact.role}) - ${contact.phone}`).join('\n')}

MAKING ENVIRONMENT SAFE:
${safetyPlan.remove_means_plan.map(step => `• ${step}`).join('\n')}

REASONS FOR LIVING:
${safetyPlan.reasons_for_living.map(reason => `• ${reason}`).join('\n')}

STEPS IF PLAN FAILS:
${safetyPlan.steps_if_plan_fails.map(step => `• ${step}`).join('\n')}
    `.trim();

    const blob = new Blob([planText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'safety-plan.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading safety plan...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-primary">Safety Plan Builder</CardTitle>
            <div className="space-x-2">
              <Button onClick={saveSafetyPlan} disabled={saving} className="bg-primary hover:bg-primary/90">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Plan'}
              </Button>
              <Button onClick={exportPlan} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Create a personalised safety plan to help you during difficult times.
          </p>
        </CardHeader>
      </Card>

      {/* Warning Signs */}
      <PlanSection
        title="1. Warning Signs"
        description="Thoughts, feelings, situations that might lead to a crisis"
        items={safetyPlan.warning_signs}
        onAdd={(value) => addItem('warning_signs', value)}
        onRemove={(index) => removeItem('warning_signs', index)}
        placeholder="e.g., Feeling hopeless, isolating myself, not sleeping..."
      />

      {/* Internal Coping */}
      <PlanSection
        title="2. Internal Coping Strategies"
        description="Things you can do on your own, without contacting others"
        items={safetyPlan.internal_coping}
        onAdd={(value) => addItem('internal_coping', value)}
        onRemove={(index) => removeItem('internal_coping', index)}
        placeholder="e.g., Deep breathing, listening to music, taking a shower..."
      />

      {/* Distraction Activities */}
      <PlanSection
        title="3. People and Places for Distraction"
        description="Social settings and activities that provide distraction"
        items={safetyPlan.distraction_activities}
        onAdd={(value) => addItem('distraction_activities', value)}
        onRemove={(index) => removeItem('distraction_activities', index)}
        placeholder="e.g., Go to a coffee shop, visit the library, walk in the park..."
      />

      {/* People to Contact */}
      <ContactSection
        title="4. People I Can Ask for Help"
        description="Friends and family members who provide support"
        contacts={safetyPlan.people_to_contact}
        onAdd={(contact) => addItem('people_to_contact', contact)}
        onRemove={(index) => removeItem('people_to_contact', index)}
        type="personal"
      />

      {/* Professional Contacts */}
      <ContactSection
        title="5. Professional or Agency Contacts"
        description="Mental health professionals and crisis services"
        contacts={safetyPlan.professional_contacts}
        onAdd={(contact) => addItem('professional_contacts', contact)}
        onRemove={(index) => removeItem('professional_contacts', index)}
        type="professional"
      />

      {/* Remove Means */}
      <PlanSection
        title="6. Making the Environment Safe"
        description="Ways to reduce access to lethal means"
        items={safetyPlan.remove_means_plan}
        onAdd={(value) => addItem('remove_means_plan', value)}
        onRemove={(index) => removeItem('remove_means_plan', index)}
        placeholder="e.g., Lock up medications, remove sharp objects, ask someone to keep items..."
      />

      {/* Reasons for Living */}
      <PlanSection
        title="7. Reasons for Living"
        description="Things that are worth living for"
        items={safetyPlan.reasons_for_living}
        onAdd={(value) => addItem('reasons_for_living', value)}
        onRemove={(index) => removeItem('reasons_for_living', index)}
        placeholder="e.g., My children, my pets, future goals, helping others..."
      />

      {/* Steps if Plan Fails */}
      <PlanSection
        title="8. If This Plan Doesn't Work"
        description="Emergency steps to take if the crisis continues"
        items={safetyPlan.steps_if_plan_fails}
        onAdd={(value) => addItem('steps_if_plan_fails', value)}
        onRemove={(index) => removeItem('steps_if_plan_fails', index)}
        placeholder="e.g., Call 999, go to emergency room, call crisis hotline..."
      />
    </div>
  );
};

interface PlanSectionProps {
  title: string;
  description: string;
  items: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder: string;
}

const PlanSection = ({ title, description, items, onAdd, onRemove, placeholder }: PlanSectionProps) => {
  const [newItem, setNewItem] = useState('');

  const handleAdd = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <p className="text-muted-foreground text-sm">{description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button onClick={handleAdd} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-muted rounded">
              <span>{item}</span>
              <Button
                onClick={() => onRemove(index)}
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive/80"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

interface ContactSectionProps {
  title: string;
  description: string;
  contacts: Array<{name: string; relationship?: string; role?: string; phone: string; preferred_contact_method?: string; notes?: string}>;
  onAdd: (contact: any) => void;
  onRemove: (index: number) => void;
  type: 'personal' | 'professional';
}

const ContactSection = ({ title, description, contacts, onAdd, onRemove, type }: ContactSectionProps) => {
  const [newContact, setNewContact] = useState({
    name: '',
    relationship: '',
    role: '',
    phone: '',
    preferred_contact_method: 'call',
    notes: ''
  });

  const handleAdd = () => {
    if (newContact.name.trim() && newContact.phone.trim()) {
      onAdd({
        ...newContact,
        [type === 'personal' ? 'relationship' : 'role']: type === 'personal' ? newContact.relationship : newContact.role
      });
      setNewContact({
        name: '',
        relationship: '',
        role: '',
        phone: '',
        preferred_contact_method: 'call',
        notes: ''
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <p className="text-muted-foreground text-sm">{description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={newContact.name}
              onChange={(e) => setNewContact({...newContact, name: e.target.value})}
              placeholder="Contact name"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={newContact.phone}
              onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
              placeholder="Phone number"
            />
          </div>
          <div>
            <Label htmlFor="rel-role">{type === 'personal' ? 'Relationship' : 'Role'}</Label>
            <Input
              id="rel-role"
              value={type === 'personal' ? newContact.relationship : newContact.role}
              onChange={(e) => setNewContact({
                ...newContact,
                [type === 'personal' ? 'relationship' : 'role']: e.target.value
              })}
              placeholder={type === 'personal' ? 'e.g., Friend, Sister' : 'e.g., Therapist, GP'}
            />
          </div>
          {type === 'personal' && (
            <div>
              <Label htmlFor="method">Preferred Contact</Label>
              <Input
                id="method"
                value={newContact.preferred_contact_method}
                onChange={(e) => setNewContact({...newContact, preferred_contact_method: e.target.value})}
                placeholder="Call, text, etc."
              />
            </div>
          )}
        </div>
        
        {type === 'professional' && (
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={newContact.notes}
              onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
              placeholder="Any additional information..."
            />
          </div>
        )}
        
        <Button onClick={handleAdd} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
        
        <div className="space-y-2">
          {contacts.map((contact, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-muted rounded">
              <div>
                <div className="font-medium">{contact.name}</div>
                <div className="text-sm text-muted-foreground">
                  {type === 'personal' ? contact.relationship : contact.role} • {contact.phone}
                </div>
                {type === 'personal' && contact.preferred_contact_method && (
                  <Badge variant="outline" className="text-xs mt-1">
                    {contact.preferred_contact_method}
                  </Badge>
                )}
                {type === 'professional' && contact.notes && (
                  <div className="text-xs text-muted-foreground mt-1">{contact.notes}</div>
                )}
              </div>
              <Button
                onClick={() => onRemove(index)}
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive/80"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};